// src/components/ProDropdown.js

import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { FaEllipsisV } from 'react-icons/fa';
import { Link }        from 'react-router-dom';
import { TabContext } from '../context/TabContext';
import { ModalContext } from '../context/ModalContext';  // Contexto para abrir/cerrar modales
import './ProDropdown.css';

import { AuthContext } from '../context/AuthContext';
import useCloseOnScroll from '../hooks/useCloseOnScroll';
import ModalProProfileEdit from './ModalProProfileEdit';  // Nuevo modal para editar perfil profesional

export default function ProDropdown() {
  const { logout } = useContext(AuthContext);
  const { activeTab } = useContext(TabContext);
  const { openModal, modalStacks } = useContext(ModalContext);  // Añadido para manejar modales

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef                    = useRef(null);
  const [menuStyle, setMenuStyle]      = useState({ top: 0, left: 0 });
  const [scrollContainer, setScrollContainer] = useState(null);

  // ─── [A] Detectar dinámicamente el contenedor scrollable ─────────────
  useEffect(() => {
    let container = null;

    // 1) Si hay un modal con scroll:
    container = document.querySelector('.modal-container');
    if (
      container &&
      container.scrollHeight > container.clientHeight &&
      /(auto|scroll)/.test(window.getComputedStyle(container).overflowY)
    ) {
      //console.log('[ProDropdown] [A] Usando .modal-container como scrollContainer', container);
      setScrollContainer(container);
      return;
    }

    // 2) Si estamos en ProPetsPage, usar su contenedor
    if (activeTab === 'prof_pets') {
      container = document.querySelector('.pro-container');
      if (
        container &&
        container.scrollHeight > container.clientHeight &&
        /(auto|scroll)/.test(window.getComputedStyle(container).overflowY)
      ) {
        //console.log('[ProDropdown] [A] Usando .pro-container como scrollContainer', container);
        setScrollContainer(container);
        return;
      }
    }

    // 3) Si estamos en ProProfilePage, usar su contenedor
    if (activeTab === 'prof_profile') {
      container = document.querySelector('.modalProfCard');
      if (
        container &&
        container.scrollHeight > container.clientHeight &&
        /(auto|scroll)/.test(window.getComputedStyle(container).overflowY)
      ) {
        //console.log('[ProDropdown] [A] Usando .pro-profile-container como scrollContainer', container);
        setScrollContainer(container);
        return;
      }
    }

    // 4) Fallback genérico: primer <div> realmente scrollable
    const allDivs = Array.from(document.querySelectorAll('div'));
    for (let div of allDivs) {
      const style = window.getComputedStyle(div);
      if (
        /(auto|scroll)/.test(style.overflowY) &&
        div.scrollHeight > div.clientHeight
      ) {
        console.log('[ProDropdown] [A] Detectado scrollContainer dinámico:', div);
        container = div;
        break;
      }
    }

    // 5) Si no encontramos ninguno, fallback a window
    if (!container) container = window;

    setScrollContainer(container);
  }, [activeTab, dropdownOpen]);

  // ─── [B] Cerrar dropdown al hacer scroll en scrollContainer ──────────
  useCloseOnScroll(
    (e) => {
      if (dropdownOpen) {
        console.log('[ProDropdown] [B] Cierro dropdown por scroll');
        setDropdownOpen(false);
      }
    },
    scrollContainer
  );

  // ─── [C] Toggle y cálculo de posición ──────────────────────────────
  const toggleDropdown = () => {
    setDropdownOpen(o => {
      console.log('[ProDropdown] toggleDropdown →', !o);
      return !o;
    });
  };

  useEffect(() => {
    if (dropdownOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuStyle({
        top:  rect.bottom + window.scrollY,
        left: rect.right  - 200 + window.scrollX
      });
    }
  }, [dropdownOpen]);

  // ─── [D] Cerrar al click fuera ─────────────────────────────────────
  useEffect(() => {
    const onClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // ─── [E] Logout y cierre ────────────────────────────────────────────
  const handleLogout = e => {
    e.stopPropagation();
    logout();
    setDropdownOpen(false);
  };

  // ─── Menú desplegable ────────────────────────────────────────────────
  const menu = (
    <div
      className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}
      style={{ ...menuStyle, position: 'absolute', zIndex: 20000 }}
    >
      {/* Opción de “Editar Perfil” para profesionales */}
      <button
        className="dropdown-item"
        onMouseDown={e => e.stopPropagation()}
        onClick={e => {
          e.stopPropagation();
          setDropdownOpen(false);

          // 1️⃣ Evitar duplicados si ya existe ModalProProfileEdit abierto
          const alreadyOpen = Object.values(modalStacks).some(stack =>
            stack.some(modal => modal.metadata?.modalType === 'proProfileEdit')
          );
          if (alreadyOpen) return;

          // 2️⃣ Abrir modal para editar perfil profesional
          openModal(
            <ModalProProfileEdit />,
            {
              tab: activeTab,
              metadata: { modalType: 'proProfileEdit' },
              overlayClassName: 'pet-detail-overlay'
            }
          );
        }}
      >
        Editar perfil
      </button>

      {/* Enlaces de política y ayuda */}
      <Link to="/terms"   className="dropdown-item" onClick={() => setDropdownOpen(false)}>
        Términos y condiciones
      </Link>
      <Link to="/privacy" className="dropdown-item" onClick={() => setDropdownOpen(false)}>
        Política de privacidad
      </Link>
      <Link to="/help"    className="dropdown-item" onClick={() => setDropdownOpen(false)}>
        Ayuda
      </Link>

      {/* Opción de “Cerrar sesión” */}
      <button
        className="dropdown-item"
        onMouseDown={e => e.stopPropagation()}
        onClick={handleLogout}
      >
        Cerrar sesión
      </button>
    </div>
  );

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button
        className="btn btn-link dropdown-toggle"
        type="button"
        onClick={toggleDropdown}
        aria-expanded={dropdownOpen}
      >
        <FaEllipsisV size={20} />
      </button>
      {dropdownOpen && ReactDOM.createPortal(menu, document.body)}
    </div>
  );
}
